import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Nav, NavItem, Panel, Tab, Image } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleRight, faBuilding, faUsers } from '@fortawesome/pro-solid-svg-icons';
import BuildoutLogo from 'logos/buildout-icon-only-latest.svg';

import { getPropertyTypesById } from 'helpers/property_types';
import { post, put } from 'helpers/api';

import Access from './access';
import PropertyTypes from './property_types';
import './styles.scss';

const tabs = {
  users: {
    icon: faUsers,
    key: 'users',
    label: 'Users',
  },
  propertyTypes: {
    icon: faBuilding,
    key: 'propertyTypes',
    label: 'Property Types',
  },
};

export default function CompanyGridSettings({ company, companyGrid: initialCompanyGrid, companyUsers, paths }) {
  const initialPropertyTypesById = getPropertyTypesById();
  for (const propertyTypeId of initialCompanyGrid.enabledPropertyTypeIds) {
    const propertyType = initialPropertyTypesById[propertyTypeId];
    propertyType.enabled = true;

    for (const subtype of Object.values(propertyType.subtypesById)) {
      subtype.enabled = initialCompanyGrid.enabledPropertySubtypeIds.indexOf(subtype.id) >= 0;
    }
  }

  const [expanded, setExpanded] = useState();
  const [companyGrid, setCompanyGrid] = useState({
    createShadowOnPropertyCreate: initialCompanyGrid.createShadowOnPropertyCreate,
    enabled: initialCompanyGrid.enabled,
    enabledForAllUsers: !initialCompanyGrid.filterUsers,
    enabledUserIds: initialCompanyGrid.allowedUserIds,
    gridId: initialCompanyGrid.gridId,
    name: initialCompanyGrid.name,
    internal: initialCompanyGrid.account.internal,
    propertyTypesById: initialPropertyTypesById,
  });

  function changeCompanyGrid(changes) {
    setCompanyGrid({ ...companyGrid, ...changes });
  }

  const pathRef = useRef();
  React.useEffect(() => {
    if (!pathRef.current) {
      pathRef.current = initialCompanyGrid.editPath || paths.companyGridCreate;
      return;
    }

    const enabledPropertyTypeIds = [];
    const enabledPropertySubtypeIds = [];
    for (const propertyType of Object.values(companyGrid.propertyTypesById)) {
      if (propertyType.enabled) {
        enabledPropertyTypeIds.push(propertyType.id);
      }

      for (const subtype of Object.values(propertyType.subtypesById)) {
        if (subtype.enabled) {
          enabledPropertySubtypeIds.push(subtype.id);
        }
      }
    }

    const method = pathRef.current === paths.companyGridCreate ? post : put;
    method(pathRef.current, {
      allowedUserIds: companyGrid.enabledUserIds,
      companyId: company.id,
      createShadowOnPropertyCreate: companyGrid.createShadowOnPropertyCreate,
      enabled: companyGrid.enabled,
      enabledPropertyTypeIds,
      enabledPropertySubtypeIds,
      filterUsers: !companyGrid.enabledForAllUsers,
      gridId: companyGrid.gridId,
    }).then((response) => {
      pathRef.current = response.editPath;
    });
  }, [companyGrid]);

  return (
    <Panel
      className="company-grid-panel"
      expanded={expanded}
      key={companyGrid.gridId}
      onToggle={() => null}
    >
      <Panel.Heading onClick={() => setExpanded(!expanded)}>
        <div className="row title">
          <div className="col-xs-8">
            {companyGrid.name}
          </div>
          <div className="col-xs-4 text-right">
            {companyGrid.internal && (
              <Image
                className="internal-grid-img align-middle"
                name="internalGridImage"
                src={BuildoutLogo}
              />
            )}
            <FontAwesomeIcon className="align-middle expand-panel-icon" icon={expanded ? faAngleDown : faAngleRight} />
          </div>
        </div>
      </Panel.Heading>

      <Panel.Collapse>
        <Panel.Body>
          <Tab.Container
            defaultActiveKey={tabs.users.key}
            id={`grid-${companyGrid.gridId}-setttings`}
          >
            <React.Fragment>
              <Nav bsStyle="pills" className="muted">
                {Object.values(tabs).map(tab => (
                  <NavItem eventKey={tab.key} key={tab.key}>
                    <FontAwesomeIcon className="mr-2" icon={tab.icon} size="lg" />
                    {tab.label}
                  </NavItem>
                ))}
              </Nav>

              <Tab.Content animation={false}>
                <Tab.Pane eventKey={tabs.users.key}>
                  <Access companyGrid={companyGrid} users={companyUsers} onChange={changeCompanyGrid} />
                </Tab.Pane>

                <Tab.Pane eventKey={tabs.propertyTypes.key}>
                  <PropertyTypes companyGrid={companyGrid} onChange={changeCompanyGrid} />
                </Tab.Pane>
              </Tab.Content>
            </React.Fragment>
          </Tab.Container>
        </Panel.Body>
      </Panel.Collapse>
    </Panel>
  );
}

CompanyGridSettings.propTypes = {
  company: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
  companyGrid: PropTypes.shape({
    account: PropTypes.shape({
      internal: PropTypes.bool,
    }),
    allowedUserIds: PropTypes.arrayOf(PropTypes.number),
    editPath: PropTypes.string,
    enabled: PropTypes.bool.isRequired,
    enabledPropertyTypeIds: PropTypes.arrayOf(PropTypes.number),
    filterUsers: PropTypes.bool,
    gridId: PropTypes.number.isRequired,
    name: PropTypes.string,
  }),
  companyUsers: PropTypes.array.isRequired,
  paths: PropTypes.shape({
    companyGridCreate: PropTypes.string.isRequired,
  }).isRequired
};
